import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  ArrowLeft, User, Mail, MapPin, Building2,
  FileText, AlertCircle, Loader2, CheckCircle2,
  UserCheck, ChevronDown, X, Phone, Zap,
  Wifi, Wrench, MessageCircle, MoreHorizontal,
  Search, Plus, type LucideIcon,
  Server, Cable, Settings, Layers, Database, IndianRupee, Calendar, FileSpreadsheet
} from 'lucide-react'
import { taskService } from '@/services/firebase/tasks'
import { engineerService, type FieldEngineer } from '@/services/firebase/engineers'
import { customersService } from '@/services/firebase/customers'
import { useAuthStore } from '@/store/authStore'
import { ROUTES, VALIDATION } from '@/constants'
import type { TaskType, TaskPriority, TaskInput, BsnlCustomer } from '@/types'
import toast from 'react-hot-toast'
import CustomerForm from '@/features/customers/components/CustomerForm'
import BuildingPicker from '@/components/BuildingPicker'

// ── Constants ────────────────────────────────────────────────────────────────
const TASK_TYPES: { value: TaskType; label: string; sub: string; Icon: LucideIcon; grad: string; glow: string }[] = [
  { value: 'new_connection', label: 'New Connection', sub: 'Install FTTH fiber',   Icon: Wifi,            grad: 'linear-gradient(135deg,#2563eb,#06b6d4)', glow: 'rgba(37,99,235,0.35)'  },
  { value: 'support',        label: 'Support',        sub: 'Fix / repair issue',   Icon: Wrench,          grad: 'linear-gradient(135deg,#f97316,#ef4444)', glow: 'rgba(249,115,22,0.35)' },
  { value: 'enquiry',        label: 'Enquiry',        sub: 'Customer query',       Icon: MessageCircle,   grad: 'linear-gradient(135deg,#8b5cf6,#6366f1)', glow: 'rgba(139,92,246,0.35)' },
  { value: 'others',         label: 'Others',         sub: 'Any other task',       Icon: MoreHorizontal,  grad: 'linear-gradient(135deg,#475569,#334155)', glow: 'rgba(71,85,105,0.35)'  },
]

const PRIORITIES: { value: TaskPriority; label: string; emoji: string; bar: string; active: string; glow: string }[] = [
  { value: 'low',    label: 'Low',    emoji: '🔵', bar: '#94a3b8', active: 'from-slate-500 to-slate-600',   glow: 'rgba(148,163,184,0.4)' },
  { value: 'medium', label: 'Medium', emoji: '🟢', bar: '#3b82f6', active: 'from-blue-500 to-blue-600',     glow: 'rgba(59,130,246,0.4)'  },
  { value: 'high',   label: 'High',   emoji: '🟠', bar: '#f97316', active: 'from-orange-500 to-orange-600', glow: 'rgba(249,115,22,0.4)'  },
  { value: 'urgent', label: 'Urgent', emoji: '🔴', bar: '#ef4444', active: 'from-red-500 to-red-600',       glow: 'rgba(239,68,68,0.4)'   },
]

// ── Shared input styles ───────────────────────────────────────────────────────
const ic = "flex-1 text-[15px] outline-none text-gray-900 placeholder-gray-400 bg-transparent"
const iw = "flex items-center gap-3 bg-white border border-gray-200 rounded-2xl px-4 py-3.5 focus-within:ring-2 focus-within:ring-blue-500/25 focus-within:border-blue-400 hover:border-gray-300 transition-all shadow-sm"
const tw = "border border-gray-200 rounded-2xl px-4 py-3.5 bg-white focus-within:ring-2 focus-within:ring-blue-500/25 focus-within:border-blue-400 hover:border-gray-300 transition-all shadow-sm"
const tc = "w-full text-[15px] outline-none text-gray-900 placeholder-gray-400 bg-transparent resize-none leading-relaxed"

const Err = ({ msg }: { msg?: string }) => msg ? (
  <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1.5 font-semibold">
    <AlertCircle size={11} className="shrink-0" />{msg}
  </p>
) : null

// ── Section header ────────────────────────────────────────────────────────────
const SectionHead = ({ grad, Icon, title, sub }: { grad: string; Icon: LucideIcon; title: string; sub?: string }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-9 h-9 rounded-2xl flex items-center justify-center shrink-0" style={{ background: grad }}>
      <Icon size={16} className="text-white" />
    </div>
    <div>
      <p className="font-black text-gray-900 text-sm leading-tight">{title}</p>
      {sub && <p className="text-[11px] text-gray-400">{sub}</p>}
    </div>
  </div>
)


// ── Page ─────────────────────────────────────────────────────────────────────
const NewTaskPage = () => {
  const navigate = useNavigate()
  const navLocation = useLocation()
  const { user } = useAuthStore()

  const initialType = (navLocation.state as { type?: TaskType })?.type || ''
  const isDocket = initialType === 'enquiry'
  const isEnquiry = isDocket
  const [taskType, setTaskType]     = useState<TaskType | ''>(initialType)
  const [customType, setCustomType] = useState('')
  const [customerName, setCustomerName]   = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [customerEmail, setCustomerEmail] = useState('')
  const [address, setAddress] = useState('')
  const [location, setLocation] = useState('')
  const [city, setCity]       = useState('')
  const [priority, setPriority] = useState<TaskPriority>('medium')
  const [description, setDescription] = useState('')
  const [notes, setNotes]             = useState('')
  const [assignedTo, setAssignedTo]               = useState('')
  const [assignedEngineerName, setAssignedEngineerName] = useState('')
  const [engineers, setEngineers]     = useState<FieldEngineer[]>([])
  const [engLoading, setEngLoading]   = useState(true)
  const [showPicker, setShowPicker]   = useState(false)
  const [engSearch, setEngSearch]     = useState('')
  const [saving, setSaving]   = useState(false)
  const [errors, setErrors]   = useState<Record<string, string>>({})

  const [allCustomers, setAllCustomers] = useState<BsnlCustomer[]>([])
  const [custSearch, setCustSearch] = useState('')
  const [showCustDropdown, setShowCustDropdown] = useState(false)
  const [showQuickAddCust, setShowQuickAddCust] = useState(false)

  // BSNL technical & billing fields
  const [serviceNumber, setServiceNumber] = useState('')
  const [bbUserId, setBbUserId] = useState('')
  const [oltIp, setOltIp] = useState('')
  const [vlan, setVlan] = useState<string>('')
  const [port, setPort] = useState<string>('')
  const [voipPassword, setVoipPassword] = useState('')
  const [exchangeCode, setExchangeCode] = useState('')
  const [category, setCategory] = useState('')
  const [subServiceType, setSubServiceType] = useState('')
  const [subscriptionPlan, setSubscriptionPlan] = useState('')
  const [planPeriod, setPlanPeriod] = useState('MONTHLY')
  const [billingAccountNo, setBillingAccountNo] = useState<string>('')
  const [fmc, setFmc] = useState<string>('')
  const [activationDate, setActivationDate] = useState('')

  const handleCustomerCreated = (newCust: BsnlCustomer) => {
    setAllCustomers(prev => [newCust, ...prev])
    handleSelectCustomer(newCust)
    setShowQuickAddCust(false)
  }

  useEffect(() => {
    engineerService.list().then(res => {
      if (res.success && res.data) setEngineers(res.data.filter(e => e.isActive && e.role === 'engineer'))
      setEngLoading(false)
    })
    customersService.list().then(res => {
      if (res.success && res.data) setAllCustomers(res.data)
    })
  }, [])

  const handleSelectCustomer = (c: BsnlCustomer) => {
    setCustomerName(c.customerName)
    setCustomerPhone(String(c.mobile))
    setCustomerEmail(c.email ?? '')
    setAddress(c.address)
    setLocation(c.building ?? '')
    setCity(c.address.toLowerCase().includes('kozhikode') ? 'Kozhikode' : 'Thiruvananthapuram')
    // Technical fields
    setServiceNumber(c.serviceNumber ?? '')
    setBbUserId(c.bbUserId ?? '')
    setOltIp(c.oltIp ?? '')
    setVlan(c.vlan != null ? String(c.vlan) : '')
    setPort(c.port != null ? String(c.port) : '')
    setVoipPassword(c.voipPassword ?? '')
    setExchangeCode(c.exchangeCode ?? '')
    setCategory(c.category ?? '')
    setSubServiceType(c.subServiceType ?? '')
    setSubscriptionPlan(c.subscriptionPlan ?? '')
    setPlanPeriod(c.planPeriod ?? 'MONTHLY')
    setBillingAccountNo(c.billingAccountNo ? String(c.billingAccountNo) : '')
    setFmc(c.fmc ? String(c.fmc) : '')
    setActivationDate(c.activationDate ?? '')
    setCustSearch('')
    setShowCustDropdown(false)
    toast.success(`Loaded details for ${c.customerName}!`)
    clrErr('customerName')
    clrErr('customerPhone')
    clrErr('address')
    clrErr('city')
  }

  const clrErr = (k: string) => setErrors(e => { const n = { ...e }; delete n[k]; return n })

  const validate = () => {
    const e: Record<string, string> = {}
    if (!taskType) e.taskType = 'Select a task type'
    if (taskType === 'others' && !customType.trim()) e.customType = 'Describe the task type'
    if (!customerName.trim()) e.customerName = 'Customer name is required'
    if (!customerPhone.trim()) e.customerPhone = 'Phone number is required'
    else if (!VALIDATION.PHONE_REGEX.test(customerPhone.replace(/\s/g, ''))) e.customerPhone = 'Enter a valid 10-digit mobile number'
    if (customerEmail && !VALIDATION.EMAIL_REGEX.test(customerEmail)) e.customerEmail = 'Enter a valid email'
    if (!address.trim()) e.address = 'Address is required'
    if (!city.trim()) e.city = 'City is required'
    return e
  }

  const handleSubmit = async () => {
    const e = validate()
    if (Object.keys(e).length) { setErrors(e); toast.error('Please fix the highlighted fields'); return }
    setSaving(true)
    try {
      const input: TaskInput = {
        type: taskType as TaskType,
        customType: taskType === 'others' ? customType.trim() : undefined,
        status: assignedTo ? 'assigned' : 'open',
        priority,
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim(),
        customerEmail: customerEmail.trim() || undefined,
        address: address.trim(),
        location: location.trim() || undefined,
        city: city.trim(),
        description: description.trim() || undefined,
        notes: notes.trim() || undefined,
        assignedTo: assignedTo || undefined,
        assignedEngineerName: assignedEngineerName || undefined,
        createdBy: user?.uid ?? user?.id ?? 'unknown',
        // BSNL technical & billing fields (from customer picker OR manually entered)
        // — only for non-docket tasks; enquiry dockets never have BSNL data at creation
        ...(!isDocket ? {
          serviceNumber: serviceNumber.trim() || undefined,
          bbUserId: bbUserId.trim() || undefined,
          oltIp: oltIp.trim() || undefined,
          vlan: vlan !== '' ? Number(vlan) : null,
          port: port !== '' ? Number(port) : null,
          category: category.trim() || undefined,
          exchangeCode: exchangeCode.trim() || undefined,
          subServiceType: subServiceType.trim() || undefined,
          subscriptionPlan: subscriptionPlan.trim() || undefined,
          planPeriod: planPeriod || undefined,
          billingAccountNo: billingAccountNo !== '' ? Number(billingAccountNo) : undefined,
          fmc: fmc !== '' ? Number(fmc) : undefined,
          voipPassword: voipPassword.trim() || undefined,
          activationDate: activationDate.trim() || undefined,
        } : {}),
      }
      const res = await taskService.create(input)
      if (!res.success) throw new Error(res.error)
      toast.success(`Task ${res.data?.taskNumber} created!`)
      navigate(ROUTES.TASKS)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create task')
    } finally {
      setSaving(false)
    }
  }

  const selectedTypeObj = TASK_TYPES.find(t => t.value === taskType)
  const selectedPriObj  = PRIORITIES.find(p => p.value === priority)!
  const filteredEngs = engineers.filter(e =>
    !engSearch || e.name.toLowerCase().includes(engSearch.toLowerCase()) ||
    e.location.toLowerCase().includes(engSearch.toLowerCase())
  )

  return (
    <div className="min-h-screen bg-gray-50/60">

      {/* ── Mobile progress strip (contextual, sits below global Header) ── */}
      <div className="lg:hidden relative -mx-4 -mt-4 mb-3 overflow-hidden"
        style={{ background: 'linear-gradient(135deg,#050d1f 0%,#0c1e3d 60%,#0d2a50 100%)' }}>
        <div className="relative z-10 flex items-center justify-between px-4 pt-2.5 pb-2">
          <p className="text-[11px] text-blue-300/60">
            {taskType
              ? <>{selectedTypeObj?.label} &middot; <span className="text-white/80">{selectedPriObj.label} Priority</span></>
              : 'Select type & fill in details'}
          </p>
          <p className="text-[11px] font-bold text-blue-300/50">
            {Math.round(([taskType, customerName, customerPhone, address, city].filter(Boolean).length / 5) * 100)}% complete
          </p>
        </div>
        <div className="h-[3px] bg-white/10 mx-4 mb-2.5 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${Math.round(([taskType, customerName, customerPhone, address, city].filter(Boolean).length / 5) * 100)}%`,
              background: selectedTypeObj?.grad ?? 'linear-gradient(135deg,#2563eb,#06b6d4)'
            }} />
        </div>
      </div>

      {/* ── Desktop heading ── */}
      <div className="hidden lg:flex items-center gap-3 mb-6 pt-0">
        <button onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-gray-200 hover:bg-gray-50 shadow-sm transition-all">
          <ArrowLeft size={16} className="text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-black text-gray-900">{isDocket ? 'New Docket Request' : 'New Task'}</h1>
          <p className="text-sm text-gray-400">{isDocket ? 'Register a new BSNL FTTH enquiry' : 'Fill in customer and task details'}</p>
        </div>
      </div>

      <div className="pt-4 pb-32 space-y-5 lg:max-w-2xl">

        {/* ── Task Type — hidden for docket requests ── */}
        {!isDocket && <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100">
          <SectionHead grad="linear-gradient(135deg,#2563eb,#06b6d4)" Icon={Zap} title="Task Type" sub="What kind of work is this?" />
          <div className="grid grid-cols-2 gap-3">
            {TASK_TYPES.map(t => {
              const active = taskType === t.value
              return (
                <button key={t.value}
                  onClick={() => { setTaskType(t.value); clrErr('taskType') }}
                  className="relative flex flex-col items-start gap-2 p-4 rounded-2xl border-2 transition-all duration-200 active:scale-95 text-left overflow-hidden"
                  style={active ? {
                    background: t.grad,
                    borderColor: 'transparent',
                    boxShadow: `0 6px 20px ${t.glow}`,
                  } : {
                    background: 'white',
                    borderColor: '#e5e7eb',
                  }}>
                  {active && (
                    <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full"
                      style={{ background: 'rgba(255,255,255,0.15)' }} />
                  )}
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${active ? 'bg-white/20' : 'bg-gray-100'}`}>
                    <t.Icon size={17} className={active ? 'text-white' : 'text-gray-500'} />
                  </div>
                  <div>
                    <p className={`text-sm font-black leading-tight ${active ? 'text-white' : 'text-gray-800'}`}>{t.label}</p>
                    <p className={`text-[10px] mt-0.5 ${active ? 'text-white/70' : 'text-gray-400'}`}>{t.sub}</p>
                  </div>
                  {active && (
                    <div className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-white/30 flex items-center justify-center">
                      <CheckCircle2 size={12} className="text-white" />
                    </div>
                  )}
                </button>
              )
            })}
          </div>
          <Err msg={errors.taskType} />

          {taskType === 'others' && (
            <div className="mt-3">
              <div className={iw}>
                <FileText size={16} className="text-gray-400 shrink-0" />
                <input value={customType}
                  onChange={e => { setCustomType(e.target.value); clrErr('customType') }}
                  placeholder="Describe the task type…" className={ic} />
              </div>
              <Err msg={errors.customType} />
            </div>
          )}
        </div>}

        {/* ── Priority ── */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100">
          <SectionHead grad="linear-gradient(135deg,#f97316,#ef4444)" Icon={Zap} title="Priority Level" sub="How urgent is this task?" />
          <div className="grid grid-cols-4 gap-2.5">
            {PRIORITIES.map(p => {
              const active = priority === p.value
              return (
                <button key={p.value} onClick={() => setPriority(p.value)}
                  className={`relative flex flex-col items-center gap-1.5 py-4 rounded-2xl border-2 transition-all duration-200 active:scale-95 overflow-hidden`}
                  style={active ? {
                    background: `linear-gradient(135deg,${p.bar},${p.bar}cc)`,
                    borderColor: 'transparent',
                    boxShadow: `0 4px 16px ${p.glow}`,
                  } : {
                    background: 'white',
                    borderColor: '#e5e7eb',
                  }}>
                  <span className="text-xl leading-none">{p.emoji}</span>
                  <span className={`text-[11px] font-black ${active ? 'text-white' : 'text-gray-600'}`}>{p.label}</span>
                  {active && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5"
                      style={{ background: 'rgba(255,255,255,0.4)' }} />
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* ── Customer Info ── */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 space-y-4">
          <SectionHead grad="linear-gradient(135deg,#2563eb,#06b6d4)" Icon={User} title="Customer Information" sub="Who is the customer?" />

          {/* Type search to load existing customer details — hidden for enquiry */}
          {!isEnquiry && (
          <div className="relative">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-[11px] font-black text-blue-500 uppercase tracking-widest">Search Existing Customer</label>
              <button
                type="button"
                onClick={() => setShowQuickAddCust(true)}
                className="inline-flex items-center gap-1 text-[11px] font-black text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100/80 px-2.5 py-1 rounded-xl transition-all active:scale-95"
              >
                <Plus size={11} /> Create New Customer
              </button>
            </div>
            <div className={iw}>
              <Search size={16} className="text-blue-500 shrink-0" />
              <input
                value={custSearch}
                onChange={e => {
                  setCustSearch(e.target.value)
                  setShowCustDropdown(true)
                }}
                onFocus={() => setShowCustDropdown(true)}
                placeholder="Type customer name, phone, or service number..."
                className={ic}
              />
              {custSearch && (
                <button
                  type="button"
                  onClick={() => {
                    setCustSearch('')
                    setShowCustDropdown(false)
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {showCustDropdown && custSearch.trim().length > 0 && (
              <div className="absolute left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 max-h-60 overflow-y-auto divide-y divide-gray-50">
                {allCustomers
                  .filter(c => {
                    const q = custSearch.toLowerCase()
                    return (
                      c.customerName.toLowerCase().includes(q) ||
                      String(c.mobile).includes(q) ||
                      c.serviceNumber.toLowerCase().includes(q)
                    )
                  })
                  .slice(0, 10)
                  .map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => handleSelectCustomer(c)}
                      className="w-full flex flex-col items-start gap-1 px-4 py-3 hover:bg-blue-50 text-left transition-colors"
                    >
                      <span className="text-sm font-bold text-gray-800">{c.customerName}</span>
                      <span className="text-xs text-gray-400">
                        📞 +91 {c.mobile} &middot; 📂 {c.serviceNumber} &middot; 🏢 {c.building || 'No building'}
                      </span>
                    </button>
                  ))}
                {allCustomers.filter(c => {
                  const q = custSearch.toLowerCase()
                  return (
                    c.customerName.toLowerCase().includes(q) ||
                    String(c.mobile).includes(q) ||
                    c.serviceNumber.toLowerCase().includes(q)
                  )
                }).length === 0 && (
                  <p className="px-4 py-3.5 text-center text-xs text-gray-400">No matching customers found</p>
                )}
              </div>
            )}
          </div>
          )}

          <div>
            <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Company Name (Customer Name) <span className="text-red-400">*</span></label>
            <div className={iw + (errors.customerName ? ' border-red-300 ring-red-200' : '')}>
              <User size={16} className="text-gray-400 shrink-0" />
              <input value={customerName}
                onChange={e => { setCustomerName(e.target.value); clrErr('customerName') }}
                placeholder="e.g. ABC Enterprises" className={ic} />
              {customerName.length > 2 && <CheckCircle2 size={16} className="text-green-500 shrink-0" />}
            </div>
            <Err msg={errors.customerName} />
          </div>

          <div>
            <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Mobile Number <span className="text-red-400">*</span></label>
            <div className={iw + (errors.customerPhone ? ' border-red-300 ring-red-200' : '')}>
              <div className="flex items-center gap-1.5 shrink-0 pr-3 border-r border-gray-200">
                <span className="text-sm">🇮🇳</span>
                <span className="text-xs font-bold text-gray-500">+91</span>
              </div>
              <input type="tel" inputMode="numeric" value={customerPhone}
                onChange={e => { setCustomerPhone(e.target.value.replace(/\D/g, '').slice(0, 10)); clrErr('customerPhone') }}
                placeholder="98765 43210" className={ic} />
              {customerPhone.length === 10 && <CheckCircle2 size={16} className="text-green-500 shrink-0" />}
            </div>
            <Err msg={errors.customerPhone} />
          </div>

          <div>
            <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Email <span className="text-gray-300 font-medium normal-case tracking-normal">(optional)</span></label>
            <div className={iw + (errors.customerEmail ? ' border-red-300' : '')}>
              <Mail size={16} className="text-gray-400 shrink-0" />
              <input type="email" value={customerEmail}
                onChange={e => { setCustomerEmail(e.target.value); clrErr('customerEmail') }}
                placeholder="customer@example.com" className={ic} />
            </div>
            <Err msg={errors.customerEmail} />
          </div>
        </div>

        {/* ── Location ── */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 space-y-4">
          <SectionHead grad="linear-gradient(135deg,#059669,#10b981)" Icon={MapPin} title="Address & Location" sub="Where is the work site?" />

          <div>
            <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Full Address <span className="text-red-400">*</span></label>
            <div className={tw + (errors.address ? ' border-red-300' : '')}>
              <textarea value={address}
                onChange={e => { setAddress(e.target.value); clrErr('address') }}
                placeholder="House/Flat No., Street, Area…" rows={2} className={tc} />
            </div>
            <Err msg={errors.address} />
          </div>

          <div>
            <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Building / Landmark <span className="text-gray-300 font-medium normal-case tracking-normal">(optional)</span></label>
            <BuildingPicker
              value={location}
              onChange={val => setLocation(val)}
              inputWrapClass={iw}
              inputClass={ic}
            />
          </div>

          <div>
            <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">City <span className="text-red-400">*</span></label>
            <div className={iw + (errors.city ? ' border-red-300' : '')}>
              <Building2 size={16} className="text-gray-400 shrink-0" />
              <input value={city} onChange={e => { setCity(e.target.value); clrErr('city') }}
                placeholder="e.g. Kozhikode" className={ic} />
            </div>
            <Err msg={errors.city} />
          </div>
        </div>

        {/* ── BSNL Connection & Billing Details ── */}
        {!isDocket && <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 space-y-4">
          <SectionHead grad="linear-gradient(135deg,#7c3aed,#8b5cf6)" Icon={Wifi} title="BSNL Connection Details" sub="Technical configuration and billing info" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Service Number</label>
              <div className={iw}>
                <Layers size={16} className="text-gray-400 shrink-0" />
                <input value={serviceNumber} onChange={e => setServiceNumber(e.target.value)} placeholder="0471-2117530" className={ic} />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Exchange Code</label>
              <div className={iw}>
                <Settings size={16} className="text-gray-400 shrink-0" />
                <input value={exchangeCode} onChange={e => setExchangeCode(e.target.value)} placeholder="TVMTPK" className={ic} />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Category</label>
              <div className={iw}>
                <Settings size={16} className="text-gray-400 shrink-0" />
                <input value={category} onChange={e => setCategory(e.target.value)} placeholder="Case IV A" className={ic} />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Sub Service Type</label>
              <div className={iw}>
                <Layers size={16} className="text-gray-400 shrink-0" />
                <input value={subServiceType} onChange={e => setSubServiceType(e.target.value)} placeholder="BHARAT FIBER COMBO" className={ic} />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">BB User ID</label>
              <div className={iw}>
                <User size={16} className="text-gray-400 shrink-0" />
                <input value={bbUserId} onChange={e => setBbUserId(e.target.value)} placeholder="al4712117530_sid" className={ic} />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">OLT IP Address</label>
              <div className={iw}>
                <Server size={16} className="text-gray-400 shrink-0" />
                <input value={oltIp} onChange={e => setOltIp(e.target.value)} placeholder="10.215.181.225" className={ic} />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">VLAN ID</label>
              <div className={iw}>
                <Cable size={16} className="text-gray-400 shrink-0" />
                <input type="number" value={vlan} onChange={e => setVlan(e.target.value)} placeholder="431" className={ic} />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Port</label>
              <div className={iw}>
                <Cable size={16} className="text-gray-400 shrink-0" />
                <input type="number" value={port} onChange={e => setPort(e.target.value)} placeholder="4" className={ic} />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">VoIP Password</label>
              <div className={iw}>
                <Settings size={16} className="text-gray-400 shrink-0" />
                <input value={voipPassword} onChange={e => setVoipPassword(e.target.value)} placeholder="e.g. 1234 or None" className={ic} />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Billing Account No.</label>
              <div className={iw}>
                <Database size={16} className="text-gray-400 shrink-0" />
                <input type="number" value={billingAccountNo} onChange={e => setBillingAccountNo(e.target.value)} placeholder="9041365663" className={ic} />
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Subscription Plan</label>
              <div className={iw}>
                <FileSpreadsheet size={16} className="text-gray-400 shrink-0" />
                <input value={subscriptionPlan} onChange={e => setSubscriptionPlan(e.target.value)} placeholder="e.g. FIBRE SUPER STAR PREMIUM PLUS OTT" className={ic} />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Plan Period</label>
              <div className={iw}>
                <Calendar size={16} className="text-gray-400 shrink-0" />
                <select value={planPeriod} onChange={e => setPlanPeriod(e.target.value)} className="flex-1 text-[15px] outline-none text-gray-900 bg-transparent">
                  <option value="MONTHLY">Monthly</option>
                  <option value="13MONTHLY">13-Monthly (Annual)</option>
                  <option value="YEARLY">Yearly</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Monthly FMC (₹)</label>
              <div className={iw}>
                <IndianRupee size={16} className="text-gray-400 shrink-0" />
                <input type="number" value={fmc} onChange={e => setFmc(e.target.value)} placeholder="999" className={ic} />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Activation Date</label>
              <div className={iw}>
                <Calendar size={16} className="text-gray-400 shrink-0" />
                <input value={activationDate} onChange={e => setActivationDate(e.target.value)} placeholder="DD/MM/YYYY hh:mm:ss" className={ic} />
              </div>
            </div>
          </div>
        </div>}

        {/* ── Task Details ── */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 space-y-4">
          <SectionHead grad="linear-gradient(135deg,#7c3aed,#a855f7)" Icon={FileText} title="Task Details" sub="Describe the work required" />

          <div>
            <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Description</label>
            <div className={tw}>
              <textarea value={description} onChange={e => setDescription(e.target.value)}
                placeholder="Describe the task, issue or requirement…" rows={3} className={tc} />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Internal Notes</label>
            <div className={tw} style={{ background: 'rgba(251,191,36,0.03)' }}>
              <textarea value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Notes for the team (not visible to customer)…" rows={2} className={tc} />
            </div>
          </div>
        </div>

        {/* ── Assign Engineer ── */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 space-y-3">
          <div className="flex items-center justify-between mb-1">
            <SectionHead grad="linear-gradient(135deg,#0ea5e9,#06b6d4)" Icon={UserCheck} title="Assign Engineer" sub="Optional — can be assigned later" />
            {assignedTo && (
              <button onClick={() => { setAssignedTo(''); setAssignedEngineerName('') }}
                className="flex items-center gap-1.5 text-[11px] font-black text-red-500 hover:text-red-700 bg-red-50 px-3 py-1.5 rounded-xl transition-colors">
                <X size={11} /> Clear
              </button>
            )}
          </div>

          {assignedTo ? (
            <div className="flex items-center gap-3 px-4 py-4 rounded-2xl"
              style={{ background: 'linear-gradient(135deg,rgba(37,99,235,0.06),rgba(6,182,212,0.06))', border: '2px solid rgba(37,99,235,0.18)' }}>
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-white text-xs font-black shrink-0"
                style={{ background: 'linear-gradient(135deg,#2563eb,#06b6d4)', boxShadow: '0 4px 12px rgba(37,99,235,0.3)' }}>
                {assignedEngineerName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-black text-blue-700">{assignedEngineerName}</p>
                <p className="text-[10px] text-blue-400 mt-0.5">Will receive a push notification</p>
              </div>
              <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center">
                <CheckCircle2 size={14} className="text-blue-500" />
              </div>
            </div>
          ) : (
            <button onClick={() => setShowPicker(p => !p)}
              className="w-full flex items-center gap-3 px-4 py-4 rounded-2xl border-2 border-dashed border-gray-200 hover:border-blue-300 hover:bg-blue-50/40 transition-all group active:scale-[0.98]">
              <div className="w-11 h-11 rounded-2xl bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center shrink-0 transition-colors">
                <Phone size={17} className="text-gray-400 group-hover:text-blue-500 transition-colors" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-black text-gray-600 group-hover:text-blue-600">Select a field engineer</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Status will be set to Assigned</p>
              </div>
              <ChevronDown size={16} className={`text-gray-400 transition-transform duration-200 ${showPicker ? 'rotate-180' : ''}`} />
            </button>
          )}

          {showPicker && !assignedTo && (
            <div className="rounded-2xl border border-gray-200 overflow-hidden shadow-md">
              <div className="flex items-center gap-2.5 px-4 py-3 border-b border-gray-100 bg-gray-50/80">
                <User size={14} className="text-gray-400 shrink-0" />
                <input value={engSearch} onChange={e => setEngSearch(e.target.value)}
                  placeholder="Search engineers by name or area…"
                  className="flex-1 text-sm outline-none text-gray-700 placeholder-gray-400 bg-transparent" autoFocus />
                {engSearch && <button onClick={() => setEngSearch('')}><X size={13} className="text-gray-400" /></button>}
              </div>
              <div className="max-h-56 overflow-y-auto">
                {engLoading ? (
                  <div className="flex items-center justify-center py-8 gap-2 text-gray-400">
                    <Loader2 size={15} className="animate-spin" />
                    <span className="text-xs">Loading engineers…</span>
                  </div>
                ) : filteredEngs.length === 0 ? (
                  <p className="py-8 text-center text-xs text-gray-400">
                    {engineers.length === 0 ? 'No active engineers found.' : 'No match.'}
                  </p>
                ) : filteredEngs.map((eng, i) => (
                  <button key={eng.id}
                    onClick={() => { setAssignedTo(eng.id); setAssignedEngineerName(eng.name); setShowPicker(false); setEngSearch('') }}
                    className={`w-full flex items-center gap-3 px-4 py-3.5 hover:bg-blue-50 transition-colors text-left group ${i > 0 ? 'border-t border-gray-50' : ''}`}>
                    <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white text-xs font-black shrink-0"
                      style={{ background: 'linear-gradient(135deg,#2563eb,#06b6d4)' }}>
                      {eng.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-800 group-hover:text-blue-700 transition-colors">{eng.name}</p>
                      <p className="text-[11px] text-gray-400">{eng.location} {eng.phoneNumber ? `· ${eng.phoneNumber}` : ''}</p>
                    </div>
                    <ChevronDown size={14} className="-rotate-90 text-gray-300 group-hover:text-blue-400 transition-colors" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Floating submit button ── */}
      <div className="fixed bottom-0 left-0 right-0 z-30 px-4 pb-6 pt-3 lg:static lg:px-0 lg:pb-0 lg:pt-0 lg:mt-2"
        style={{ background: 'linear-gradient(to top, rgba(249,250,251,1) 60%, transparent)' }}>
        <button onClick={handleSubmit} disabled={saving}
          className="w-full lg:max-w-2xl flex items-center justify-center gap-3 py-5 text-white font-black rounded-3xl text-[15px] transition-all duration-200 disabled:opacity-60 active:scale-[0.97] shadow-2xl"
          style={{
            background: selectedTypeObj?.grad ?? 'linear-gradient(135deg,#2563eb,#1d4ed8)',
            boxShadow: `0 8px 32px ${selectedTypeObj?.glow ?? 'rgba(37,99,235,0.4)'}`,
          }}>
          {saving ? (
            <><Loader2 size={20} className="animate-spin" /> {isDocket ? 'Creating Docket…' : 'Creating Task…'}</>
          ) : assignedTo ? (
            <><UserCheck size={20} /> {isDocket ? 'Create Docket & Assign to' : 'Create & Assign to'} {assignedEngineerName.split(' ')[0]}</>
          ) : (
            <><CheckCircle2 size={20} /> {isDocket ? 'Create Docket Request' : 'Create Task'}</>
          )}
        </button>
      </div>
      {showQuickAddCust && (
        <CustomerForm
          onClose={() => setShowQuickAddCust(false)}
          onSaved={handleCustomerCreated}
        />
      )}
    </div>
  )
}

export default NewTaskPage
